import { useMemo, useRef } from 'react';
import { Animated } from 'react-native';

import { isWebPlatform } from '../../boot/utils';

/**
 * Use Animated.event to create a scrollHandler,
 * which should be able to modify scrollY while scrolling.
 *
 * onScroll will be listening if provided.
 */
export const scrollHandler = (
  scrollY: Animated.Value,
  onScroll?: (event) => void
) =>
  Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
    useNativeDriver: !isWebPlatform,
    listener: typeof onScroll === 'function' ? onScroll : undefined,
  });

export const useCoverProps = (coverHeight, onScroll) => {
  const scrollY = useRef(new Animated.Value(0));

  /**
   * The cover will be moving with scrolling,
   *
   * Each range can be modified if UED ask for.
   */
  const translateY = useMemo(() => {
    return scrollY.current.interpolate({
      inputRange: [0, coverHeight],
      outputRange: [0, -coverHeight],
      extrapolate: 'clamp',
    });
  }, [coverHeight]);

  /**
   * If scroll to a negative value (such as ios pull refresh),
   * the cover will be scaling up.
   *
   * Each range can be modified if UED ask for.
   */
  const scale = useMemo(() => {
    return scrollY.current.interpolate({
      inputRange: [-coverHeight, 0],
      outputRange: [3, 1],
      extrapolate: 'clamp',
    });
  }, [coverHeight]);

  const style = useMemo(() => {
    return { transform: [{ translateY }, { scale }] };
  }, [translateY, scale]);

  const handleOnScroll = useMemo(() => {
    return scrollHandler(scrollY.current, onScroll);
  }, [onScroll]);

  return { style, handleOnScroll };
};
